import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Heart, ArrowLeft, Calendar, Edit, Printer, Pencil, Plus, ChevronDown, ChevronUp, History, CheckCircle2, Clock, Sparkles, Wand2, Target, Package, Loader2, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import AIFeedbackPopup, { shouldShowFeedback } from '@/components/AIFeedbackPopup';
import { shareActivityPdf } from '@/lib/shareActivityPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Helper to check if a phrase is a question
const isQuestion = (phrase) => {
  if (!phrase) return false;
  const trimmed = phrase.trim();
  return trimmed.endsWith('?') || 
    /^(what|where|when|who|why|how|can|do|does|is|are|will|would|could|should)\s/i.test(trimmed);
};

const adaptLanguageForStage = (examples, stage) => {
  if (!examples || examples.length === 0) return [];
  
  // Filter out questions for stages 1-4 (questions not appropriate until after stage 4)
  const filteredExamples = stage <= 4 
    ? examples.filter(ex => !isQuestion(ex))
    : examples;
  
  if (stage === 1) {
    // Stage 1: Complete gestalts only (no questions)
    return filteredExamples.filter(ex => ex.split(' ').length >= 2);
  } else if (stage === 2) {
    // Stage 2: Gestalts + mitigated versions
    const baseGestalts = filteredExamples.filter(ex => ex.split(' ').length >= 2);
    const mitigated = [];
    for (let gestalt of baseGestalts.slice(0, 2)) {
      const words = gestalt.split(' ');
      if (words.length >= 2) {
        mitigated.push(`${words[0]} ${words[1]}...`);
      }
    }
    return [...baseGestalts.slice(0, 2), ...mitigated];
  } else if (stage === 3) {
    // Stage 3: Single words
    const singleWords = new Set();
    filteredExamples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      words.forEach(w => {
        if (w.length > 2) singleWords.add(w.toLowerCase());
      });
    });
    return Array.from(singleWords).slice(0, 5);
  } else if (stage === 4) {
    // Stage 4: 2-word combinations (no questions)
    const twoWord = new Set();
    filteredExamples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      if (words.length >= 2) {
        twoWord.add(`${words[0]} ${words[1]}`);
      }
    });
    return Array.from(twoWord).slice(0, 5);
  } else {
    // Stage 5-6: Full sentences including questions
    return examples; // Use original examples (with questions) for later stages
  }
};

const getStageTip = (stage) => {
  const tips = {
    1: "Repeat these complete phrases consistently during activities. Your child is learning language in whole chunks.",
    2: "Model full phrases and celebrate when your child mixes parts from different gestalts. This shows language processing!",
    3: "Emphasize these single words clearly. Point to objects and name them. Single words are emerging from gestalts.",
    4: "Model these 2-word combinations naturally. Your child is starting to create their own novel phrases!",
    5: "Use these phrases with proper grammar. Expand on your child's attempts by adding one word.",
    6: "Model complex sentences and support storytelling. Your child can handle advanced language structures."
  };
  return tips[stage] || '';
};

export default function WeeklyFocusPage({ onLogout }) {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [focus, setFocus] = useState(null);
  const [focusHistory, setFocusHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [allActivities, setAllActivities] = useState([]);
  const [customizedActivities, setCustomizedActivities] = useState({});
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  const [activityToCustomize, setActivityToCustomize] = useState(null);
  const [customizeForm, setCustomizeForm] = useState({
    name: '',
    language_examples: '',
    follow_lead_guidance: '',
    notes: ''
  });
  const [recentGoals, setRecentGoals] = useState([]);
  const [sharingActivityId, setSharingActivityId] = useState(null);
  const [formData, setFormData] = useState({
    week_start_date: '',
    focus_statement: '',
    suggested_activities: '',
    activity_ids: [],
    modeled_language: '',
    reassurance_message: ''
  });
  const user = JSON.parse(localStorage.getItem('user'));
  
  // AI Activity Generator state
  const [showAIGeneratorDialog, setShowAIGeneratorDialog] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showAIFeedback, setShowAIFeedback] = useState(false);
  const [aiFormData, setAiFormData] = useState({
    child_interests: '',
    communication_style: 'GLP',
    regulation_preference: 'none',
    selected_goals: [],
    custom_goal: '',
    materials: '',
    age: ''
  });
  const [childGoals, setChildGoals] = useState([]);
  // Local-storage key for the in-progress draft so an interrupted edit
  // (phone lock, tab close, accidental refresh) doesn't lose work.
  const draftKey = `pf_wf_draft_${childId || 'unknown'}`;
  const [draftResumed, setDraftResumed] = useState(false);

  // Auto-save the current form to localStorage whenever it changes AND the
  // dialog is open. Skipping when the dialog is closed keeps stale data
  // from accumulating after a successful save.
  useEffect(() => {
    if (!showDialog) return;
    // Don't bother saving an empty form.
    const hasContent =
      (formData.focus_statement || '').trim() ||
      (formData.modeled_language || '').trim() ||
      (formData.suggested_activities || '').trim() ||
      (formData.activity_ids || []).length > 0;
    if (!hasContent) return;
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          formData,
          selectedActivities,
          isEdit,
          savedAt: new Date().toISOString(),
        })
      );
    } catch {/* localStorage may throw in private/incognito mode — silent */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, selectedActivities, showDialog]);

  // On mount, look for a stale draft. If one exists for this child AND we
  // didn't just arrive from a SOAP-suggestion deep link (handled separately
  // above), offer the therapist a one-tap resume.
  useEffect(() => {
    if (loading) return;
    if (draftResumed) return;
    if (searchParams.get('activity')) return;  // deep link path owns the form
    let raw;
    try { raw = localStorage.getItem(draftKey); } catch { return; }
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (!parsed?.formData) return;
    const ageHours = parsed.savedAt
      ? (Date.now() - new Date(parsed.savedAt).getTime()) / 3600000
      : 999;
    // Drop drafts older than 72h — most are abandoned by then.
    if (ageHours > 72) {
      try { localStorage.removeItem(draftKey); } catch {/**/}
      return;
    }
    toast.message('Resume your draft?', {
      description: 'Found an unsaved Weekly Focus from earlier. Tap to continue where you left off.',
      duration: 12000,
      action: {
        label: 'Resume',
        onClick: () => {
          setFormData(parsed.formData);
          setSelectedActivities(parsed.selectedActivities || []);
          setIsEdit(!!parsed.isEdit);
          setShowDialog(true);
          setDraftResumed(true);
        },
      },
      cancel: {
        label: 'Discard',
        onClick: () => {
          try { localStorage.removeItem(draftKey); } catch {/**/}
        },
      },
    });
    setDraftResumed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, childId]);

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {/**/}
  };

  // Compute which activities have been sent before
  const previouslySentActivities = focusHistory.reduce((acc, focus) => {
    (focus.activity_ids || []).forEach(id => {
      if (!acc[id]) {
        acc[id] = {
          count: 0,
          lastSent: null
        };
      }
      acc[id].count += 1;
      if (!acc[id].lastSent || focus.week_start_date > acc[id].lastSent) {
        acc[id].lastSent = focus.week_start_date;
      }
    });
    return acc;
  }, {});

  useEffect(() => {
    fetchData();
    fetchActivities();
    fetchCustomizations();
    fetchFocusHistory();
    fetchRecentGoals();
  }, [childId]);

  // 1-tap activity → Weekly Focus. When the SOAP "suggested activities" modal
  // routes the therapist here with ?activity=<id>, we wait for activities +
  // child to load, then auto-open the create dialog with that activity
  // preselected. The query param is consumed (cleared) so a refresh doesn't
  // re-trigger.
  useEffect(() => {
    const activityId = searchParams.get('activity');
    if (!activityId) return;
    if (loading) return;
    if (!allActivities.length) return;
    if (user.role !== 'therapist') return;

    const exists = allActivities.some((a) => a.id === activityId);
    if (!exists) {
      // Activity not in this therapist's bank — silently consume the param.
      setSearchParams({}, { replace: true });
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    setFormData({
      week_start_date: today,
      focus_statement: '',
      suggested_activities: '',
      activity_ids: [activityId],
      modeled_language: '',
      reassurance_message:
        "Remember: connection over perfection. Follow your child's lead and celebrate every moment of engagement.",
    });
    setSelectedActivities([activityId]);
    setShowDialog(true);
    setSearchParams({}, { replace: true });
    toast.success('Activity preselected — finish writing this week\'s focus.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allActivities, searchParams]);

  const fetchRecentGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch progress/SOAP notes for this child to get goals worked on
      const response = await axios.get(`${API}/progress/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract goals from progress notes, most recent first
      const allGoals = [];
      response.data.forEach(note => {
        // Get goals from goals_addressed field
        if (note.goals_addressed && note.goals_addressed.length > 0) {
          note.goals_addressed.forEach(goal => {
            if (goal && goal.trim()) {
              allGoals.push({
                goal: goal.trim(),
                date: note.date,
                category: note.category || 'general'
              });
            }
          });
        }
        // Also check the plan field for goal-related content
        if (note.plan && note.plan.trim()) {
          // Only add if it looks like a goal (contains action words)
          const planText = note.plan.trim();
          if (planText.length > 10 && planText.length < 200) {
            allGoals.push({
              goal: planText,
              date: note.date,
              category: note.category || 'plan',
              isFromPlan: true
            });
          }
        }
      });
      
      // Remove duplicates and take most recent 10
      const uniqueGoals = [];
      const seenGoals = new Set();
      for (const g of allGoals) {
        const normalized = g.goal.toLowerCase().substring(0, 50);
        if (!seenGoals.has(normalized)) {
          seenGoals.add(normalized);
          uniqueGoals.push(g);
        }
      }
      
      setRecentGoals(uniqueGoals.slice(0, 10));
    } catch (error) {
      console.error('Failed to load goals from SOAP notes:', error);
    }
  };

  const fetchCustomizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activities/customized/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Convert to map for easy lookup by original_activity_id
      const customMap = {};
      response.data.forEach(c => {
        customMap[c.original_activity_id] = c;
      });
      setCustomizedActivities(customMap);
    } catch (error) {
      console.error('Failed to load customizations');
    }
  };

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllActivities(response.data);
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const fetchFocusHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/weekly-focus/${childId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFocusHistory(response.data);
    } catch (error) {
      console.error('Failed to load focus history');
    }
  };

  // Fetch child's goals and interests for AI generator
  const fetchChildGoalsForAI = async () => {
    if (!child) return;
    
    // Get goals from child's therapy_goals and from recent SOAP notes
    const goals = [];
    
    // Add therapy goals from child profile
    if (child.therapy_goals && child.therapy_goals.length > 0) {
      goals.push(...child.therapy_goals);
    }
    
    // Add recent goals from SOAP notes
    recentGoals.forEach(g => {
      if (!g.isFromPlan && !goals.includes(g.goal)) {
        goals.push(g.goal);
      }
    });
    
    setChildGoals(goals);
    
    // Pre-fill interests from child profile
    const interests = child.interests?.join(', ') || '';
    setAiFormData(prev => ({
      ...prev,
      child_interests: interests,
      communication_style: child.language_learner_profile || 'GLP',
      age: child.age ? String(child.age) : ''
    }));
  };

  // Open AI generator dialog and load child data
  const openAIGenerator = () => {
    fetchChildGoalsForAI();
    setShowAIGeneratorDialog(true);
  };

  // Generate activity using AI
  const handleAIGenerate = async (e) => {
    e?.preventDefault();
    setAiGenerating(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        child_interests: aiFormData.child_interests ? aiFormData.child_interests.split(',').map(i => i.trim()).filter(i => i) : [],
        communication_style: aiFormData.communication_style,
        regulation_preference: aiFormData.regulation_preference === 'none' ? null : aiFormData.regulation_preference,
        goals: aiFormData.selected_goals || [],
        custom_goal: aiFormData.custom_goal || '',
        materials: aiFormData.materials || '',
        child_id: childId,
        glp_stage: child?.glp_stage || null,
        child_age: aiFormData.age ? parseInt(aiFormData.age) : (child?.age || null)
      };
      
      const response = await axios.post(`${API}/activities/generate-ai`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(`Created: ${response.data.name}!`);
      
      // Refresh activities and automatically select the new one
      await fetchActivities();
      
      // Add the new activity to selected activities
      setSelectedActivities(prev => [...prev, response.data.id]);
      
      setShowAIGeneratorDialog(false);
      if (shouldShowFeedback('activity_generation')) {
        setShowAIFeedback(true);
      }
      
      // Reset form
      setAiFormData({
        child_interests: child?.interests?.join(', ') || '',
        communication_style: child?.language_learner_profile || 'GLP',
        regulation_preference: 'none',
        selected_goals: [],
        custom_goal: '',
        materials: '',
        age: child?.age ? String(child.age) : ''
      });
      
    } catch (error) {
      console.error('AI generation error:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to generate activity';
      toast.error(errorDetail);
    } finally {
      setAiGenerating(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const childRes = await axios.get(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(childRes.data);

      try {
        const focusRes = await axios.get(`${API}/weekly-focus/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFocus(focusRes.data);
        setIsEdit(true);
      } catch (error) {
        setFocus(null);
        setIsEdit(false);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Friendly nudge: weekly focus is much more impactful when paired with a
    // concrete activity from the bank. If neither activity_ids nor a
    // freeform suggested_activities text is set, ask the therapist to confirm.
    const hasActivity =
      (formData.activity_ids && formData.activity_ids.length > 0) ||
      (formData.suggested_activities && formData.suggested_activities.trim() !== '');
    if (!hasActivity) {
      const proceed = window.confirm(
        "You haven't picked any activities for this week's focus.\n\n" +
        "Parents are 3× more likely to engage when there's a specific activity to try.\n\n" +
        "Send anyway?"
      );
      if (!proceed) return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        child_id: childId,
        week_start_date: formData.week_start_date,
        focus_statement: formData.focus_statement,
        suggested_activities: formData.suggested_activities.split('\n').filter(a => a.trim()),
        activity_ids: formData.activity_ids,
        modeled_language: formData.modeled_language.split('\n').filter(l => l.trim()),
        reassurance_message: formData.reassurance_message
      };

      // Always create a new focus (to preserve history)
      await axios.post(`${API}/weekly-focus`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('New weekly focus created!');

      clearDraft();
      setShowDialog(false);
      fetchData();
      fetchFocusHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save weekly focus');
    }
  };

  const handlePrint = () => {
    // Create a printable version of the content
    const printContent = document.querySelector('[data-testid="focus-card"]');
    if (!printContent) {
      toast.error('Nothing to print');
      return;
    }
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }
    
    const styles = `
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #4a6355; margin-bottom: 8px; }
        h2 { color: #4a6355; font-size: 1.5rem; margin-top: 24px; }
        h3 { color: #4a6355; font-size: 1.25rem; margin-top: 20px; }
        .header { border-bottom: 2px solid #e5e5e5; padding-bottom: 16px; margin-bottom: 24px; }
        .section { margin-bottom: 24px; }
        .activity-item { display: flex; align-items: flex-start; gap: 12px; margin: 8px 0; }
        .activity-number { background: #c6856a; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; }
        .language-phrase { background: #f0f5f2; padding: 12px 16px; border-radius: 8px; margin: 8px 0; }
        .guidance { background: #f3e8ff; padding: 16px; border-radius: 8px; margin-top: 16px; }
        .reassurance { background: #fef3e8; padding: 24px; border-radius: 12px; margin-top: 32px; font-style: italic; text-align: center; font-size: 1.1rem; color: #7a5f3a; }
        @media print { body { padding: 20px; } }
      </style>
    `;
    
    const childName = child?.name || 'Child';
    const weekDate = focus?.week_start_date ? new Date(focus.week_start_date).toLocaleDateString() : '';
    
    let activitiesHtml = '';
    if (focus?.suggested_activities?.length > 0) {
      activitiesHtml = focus.suggested_activities.map((activity, i) => 
        `<div class="activity-item"><span class="activity-number">${i + 1}</span><span>${activity}</span></div>`
      ).join('');
    }
    
    let languageHtml = '';
    if (focus?.modeled_language?.length > 0) {
      languageHtml = focus.modeled_language.map(phrase => 
        `<div class="language-phrase">"${phrase}"</div>`
      ).join('');
    }
    
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Weekly Focus - ${childName}</title>
          ${styles}
        </head>
        <body>
          <div class="header">
            <h1>Weekly Focus - ${childName}</h1>
            <p>PlayFirst Therapy | Week of ${weekDate}</p>
          </div>
          
          <div class="section">
            <h2>Focus</h2>
            <p>${focus?.focus_statement || ''}</p>
          </div>
          
          ${activitiesHtml ? `
          <div class="section">
            <h3>Activities to Try</h3>
            ${activitiesHtml}
          </div>
          ` : ''}
          
          ${languageHtml ? `
          <div class="section">
            <h3>Language to Model</h3>
            ${languageHtml}
          </div>
          ` : ''}
          
          ${child?.glp_stage ? `
          <div class="guidance">
            <strong>Stage ${child.glp_stage} Guidance:</strong> ${getStageTip(child.glp_stage)}
          </div>
          ` : ''}
          
          <div class="reassurance">
            ${focus?.reassurance_message || ''}
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
    };
    
    // Fallback for browsers that don't fire onload
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const openEditDialog = () => {
    // Always start fresh for a new weekly focus
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      week_start_date: today,
      focus_statement: '',
      suggested_activities: '',
      activity_ids: [],
      modeled_language: '',
      reassurance_message: 'Remember: connection over perfection. Follow your child\'s lead and celebrate every moment of engagement.'
    });
    setSelectedActivities([]);
    setShowDialog(true);
  };

  const openEditFromHistory = (historyItem) => {
    // Pre-fill form with data from a previous focus
    setFormData({
      week_start_date: new Date().toISOString().split('T')[0], // New date
      focus_statement: historyItem.focus_statement,
      suggested_activities: historyItem.suggested_activities.join('\n'),
      activity_ids: historyItem.activity_ids || [],
      modeled_language: historyItem.modeled_language.join('\n'),
      reassurance_message: historyItem.reassurance_message
    });
    setSelectedActivities(historyItem.activity_ids || []);
    setShowDialog(true);
  };

  const toggleActivitySelection = (activityId) => {
    const newSelected = selectedActivities.includes(activityId)
      ? selectedActivities.filter(id => id !== activityId)
      : [...selectedActivities, activityId];
    
    setSelectedActivities(newSelected);
    setFormData({ ...formData, activity_ids: newSelected });
  };

  const openCustomizeDialog = (activity) => {
    const existing = customizedActivities[activity.id];
    if (existing) {
      setCustomizeForm({
        name: existing.name,
        language_examples: existing.language_examples.join('\n'),
        follow_lead_guidance: existing.follow_lead_guidance,
        notes: existing.notes || ''
      });
    } else {
      setCustomizeForm({
        name: activity.name,
        language_examples: activity.language_examples.join('\n'),
        follow_lead_guidance: activity.follow_lead_guidance,
        notes: ''
      });
    }
    setActivityToCustomize(activity);
    setShowCustomizeDialog(true);
  };

  const handleSaveCustomization = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/activities/customize`, {
        original_activity_id: activityToCustomize.id,
        child_id: childId,
        name: customizeForm.name,
        language_examples: customizeForm.language_examples.split('\n').filter(l => l.trim()),
        follow_lead_guidance: customizeForm.follow_lead_guidance,
        notes: customizeForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Customized "${customizeForm.name}" for ${child?.name}`);
      setShowCustomizeDialog(false);
      fetchCustomizations();
    } catch (error) {
      toast.error('Failed to save customization');
    }
  };

  // Helper to get activity with customizations applied
  const getActivityForChild = (activity) => {
    const customized = customizedActivities[activity.id];
    if (customized) {
      return {
        ...activity,
        name: customized.name,
        language_examples: customized.language_examples,
        follow_lead_guidance: customized.follow_lead_guidance,
        notes: customized.notes,
        isCustomized: true
      };
    }
    return { ...activity, isCustomized: false };
  };

  const handleShareActivity = async (activity) => {
    if (sharingActivityId) return;
    setSharingActivityId(activity.id);
    try {
      const displayActivity = getActivityForChild(activity);
      const result = await shareActivityPdf({
        activityId: activity.id,
        // Pass the child so the backend overlays the customised version too.
        childId: child?.id,
        activityName: displayActivity.name,
        childFirstName: child?.name?.split(' ')[0],
      });
      if (result === 'shared') {
        toast.success('Shared!');
      } else if (result === 'downloaded') {
        toast.success('PDF downloaded — attach it to your message.');
      } else if (result === 'error') {
        toast.error("Couldn't prepare the PDF. Try again in a moment.");
      }
    } finally {
      setSharingActivityId(null);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                This week's focus
              </h2>
              <p className="text-lg text-stone-600">{child?.name}</p>
            </div>
            <div className="flex gap-2 no-print">
              {focusHistory.length > 1 && (
                <Button
                  onClick={() => setShowHistory(!showHistory)}
                  data-testid="toggle-history-btn"
                  variant="outline"
                  className="rounded-full"
                  type="button"
                >
                  <History className="w-4 h-4 mr-2" />
                  {showHistory ? 'Hide History' : 'History'}
                  {showHistory ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                </Button>
              )}
              {focus && (
                <Button
                  onClick={handlePrint}
                  data-testid="print-focus-btn"
                  variant="outline"
                  className="rounded-full"
                  type="button"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              )}
              {user.role === 'therapist' && (
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-focus-btn"
                      onClick={openEditDialog}
                      className="rounded-full shadow-lg shadow-primary/20"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Focus
                    </Button>
                  </DialogTrigger>
                <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="focus-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">
                      Create New Weekly Focus
                    </DialogTitle>
                  </DialogHeader>
                  
                  {/* Recent Goals from SOAP Notes */}
                  {/* Child Profile Goals (canonical, persistent) */}
                  {child?.therapy_goals?.length > 0 && (
                    <div
                      className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl"
                      data-testid="profile-goals-section"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-emerald-900">Goals from {child.name}'s profile</h3>
                          <p className="text-xs text-emerald-700">Tap to add to this week's focus statement</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {child.therapy_goals.map((g, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              const currentStatement = formData.focus_statement;
                              if (!currentStatement.includes(g)) {
                                setFormData({
                                  ...formData,
                                  focus_statement: currentStatement
                                    ? `${currentStatement}\n\nGoal: ${g}`
                                    : `Goal: ${g}`,
                                });
                                toast.success('Goal added');
                              } else {
                                toast.info('Already added');
                              }
                            }}
                            className="px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-sm text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300 transition-all text-left"
                            data-testid={`profile-goal-chip-${idx}`}
                          >
                            {g.length > 60 ? `${g.substring(0, 60)}...` : g}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {recentGoals.length > 0 && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-amber-900">Recent Goals from Session Notes</h3>
                          <p className="text-xs text-amber-700">Click a goal to add it to your focus statement</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {recentGoals.map((item, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              // Add goal to focus statement
                              const currentStatement = formData.focus_statement;
                              const goalText = item.goal;
                              if (!currentStatement.includes(goalText)) {
                                setFormData({
                                  ...formData,
                                  focus_statement: currentStatement 
                                    ? `${currentStatement}\n\nGoal: ${goalText}`
                                    : `Goal: ${goalText}`
                                });
                                toast.success('Goal added to focus statement');
                              } else {
                                toast.info('Goal already in focus statement');
                              }
                            }}
                            className="px-3 py-1.5 bg-white border border-amber-200 rounded-full text-sm text-amber-800 hover:bg-amber-100 hover:border-amber-300 transition-all text-left"
                            data-testid={`goal-chip-${idx}`}
                          >
                            {item.goal.length > 60 ? `${item.goal.substring(0, 60)}...` : item.goal}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="week-start">Week starting</Label>
                      <Input
                        id="week-start"
                        data-testid="week-start-input"
                        type="date"
                        value={formData.week_start_date}
                        onChange={(e) => setFormData({ ...formData, week_start_date: e.target.value })}
                        required
                        className="h-12 rounded-2xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="focus-statement">Focus statement</Label>
                      <Textarea
                        id="focus-statement"
                        data-testid="focus-statement-input"
                        placeholder="This week, we're exploring turn-taking through playful routines..."
                        value={formData.focus_statement}
                        onChange={(e) => setFormData({ ...formData, focus_statement: e.target.value })}
                        required
                        className="rounded-2xl min-h-24"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="activities">Suggested activities (one per line)</Label>
                      <Textarea
                        id="activities"
                        data-testid="activities-input"
                        placeholder="Bubble play\nCar ramp\nPretend cooking"
                        value={formData.suggested_activities}
                        onChange={(e) => setFormData({ ...formData, suggested_activities: e.target.value })}
                        className="rounded-2xl min-h-24"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Select activities for home practice (optional)</Label>
                      <p className="text-xs text-stone-500 mb-2">
                        Choose specific activities from the library for parents to do at home. Click the pencil to customize for {child?.name}.
                      </p>
                      <div className="flex items-center gap-2 mb-2 text-xs">
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="w-3 h-3" /> Previously sent
                        </span>
                        <span className="text-stone-400">|</span>
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="w-3 h-3" /> New
                        </span>
                      </div>
                      <div className="max-h-64 overflow-y-auto border border-stone-200 rounded-2xl p-3 space-y-2">
                        {allActivities.length === 0 ? (
                          <p className="text-sm text-stone-500 text-center py-4">No activities available</p>
                        ) : (
                          <TooltipProvider>
                          {allActivities.map(activity => {
                            const displayActivity = getActivityForChild(activity);
                            const sentInfo = previouslySentActivities[activity.id];
                            const wasSentBefore = !!sentInfo;
                            
                            return (
                              <div
                                key={activity.id}
                                className={`p-3 rounded-xl transition-all ${
                                  selectedActivities.includes(activity.id)
                                    ? 'bg-primary/10 border-2 border-primary'
                                    : wasSentBefore 
                                      ? 'bg-amber-50 border-2 border-amber-200 hover:border-amber-300'
                                      : 'bg-stone-50 border-2 border-transparent hover:border-stone-300'
                                }`}
                                data-testid={`activity-select-${activity.id}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div 
                                    className="flex-1 cursor-pointer"
                                    onClick={() => toggleActivitySelection(activity.id)}
                                  >
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-stone-900">{displayActivity.name}</p>
                                      {displayActivity.isCustomized && (
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                          Customized
                                        </span>
                                      )}
                                      {wasSentBefore && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1 cursor-help">
                                              <Clock className="w-3 h-3" />
                                              Sent {sentInfo.count}x
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Last sent: {new Date(sentInfo.lastSent).toLocaleDateString()}</p>
                                            <p>Total times: {sentInfo.count}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                    <p className="text-xs text-stone-500">{activity.play_type}</p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCustomizeDialog(activity);
                                    }}
                                    className="rounded-full h-8 w-8 p-0"
                                    data-testid={`customize-activity-${activity.id}`}
                                  >
                                    <Pencil className={`w-4 h-4 ${displayActivity.isCustomized ? 'text-purple-600' : 'text-stone-400'}`} />
                                  </Button>
                                  {selectedActivities.includes(activity.id) && (
                                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                      <span className="text-white text-xs">✓</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          </TooltipProvider>
                        )}
                      </div>
                      {selectedActivities.length > 0 && (
                        <p className="text-xs text-primary mt-2">
                          {selectedActivities.length} {selectedActivities.length === 1 ? 'activity' : 'activities'} selected
                        </p>
                      )}
                      
                      {/* AI Activity Generator Button */}
                      <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-purple-900">Need a new activity?</p>
                              <p className="text-xs text-purple-700">Generate one with AI based on {child?.name}'s interests</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={openAIGenerator}
                            variant="outline"
                            className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-100"
                            data-testid="inline-ai-generator-btn"
                          >
                            <Wand2 className="w-4 h-4 mr-2" />
                            Generate
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="language">Language to model (one per line)</Label>
                      <Textarea
                        id="language"
                        data-testid="language-input"
                        placeholder="My turn\nYour turn\nReady, set, go!"
                        value={formData.modeled_language}
                        onChange={(e) => setFormData({ ...formData, modeled_language: e.target.value })}
                        className="rounded-2xl min-h-24"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reassurance">Reassurance message</Label>
                      <Textarea
                        id="reassurance"
                        data-testid="reassurance-input"
                        placeholder="Remember: connection over perfection..."
                        value={formData.reassurance_message}
                        onChange={(e) => setFormData({ ...formData, reassurance_message: e.target.value })}
                        required
                        className="rounded-2xl min-h-24"
                      />
                    </div>
                    <Button type="submit" data-testid="save-focus-btn" className="w-full h-12 rounded-full">
                      Create New Focus
                    </Button>
                  </form>
                </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Focus History Section */}
          {showHistory && focusHistory.length > 1 && (
            <Card className="p-6 bg-stone-50 rounded-3xl border border-stone-200 mb-6" data-testid="focus-history">
              <h3 className="text-lg font-serif text-primary-900 mb-4 flex items-center gap-2">
                <History className="w-5 h-5" />
                Focus History ({focusHistory.length} entries)
              </h3>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {focusHistory.map((item, index) => (
                  <div 
                    key={item.id} 
                    className={`p-4 rounded-xl border ${index === 0 ? 'bg-primary/5 border-primary/20' : 'bg-white border-stone-200'}`}
                    data-testid={`history-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-stone-700">
                            Week of {new Date(item.week_start_date).toLocaleDateString()}
                          </span>
                          {index === 0 && (
                            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">Current</span>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 line-clamp-2">{item.focus_statement}</p>
                      </div>
                      {user.role === 'therapist' && index > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditFromHistory(item)}
                          className="rounded-full ml-2"
                          title="Use as template for new focus"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {!focus ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No weekly focus yet</h3>
              <p className="text-stone-500 mb-6">
                {user.role === 'therapist'
                  ? 'Create a weekly focus to guide therapy and home practice'
                  : 'Your therapist will set this week\'s focus soon'}
              </p>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Print-only header */}
              <div className="hidden print:block mb-6">
                <h1 className="text-3xl font-serif text-primary-900 mb-2">
                  Weekly Focus - {child?.name}
                </h1>
                <p className="text-base text-stone-600">PlayFirst Therapy</p>
              </div>
              
              <Card className="p-8 bg-white rounded-3xl border-2 border-primary/20 shadow-sm" data-testid="focus-card">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-xs font-bold tracking-wide uppercase text-stone-400">Week of</p>
                    <p className="text-lg text-stone-700">{new Date(focus.week_start_date).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-serif text-primary-900 mb-3">Focus</h3>
                    <p className="text-lg text-stone-700 leading-relaxed">{focus.focus_statement}</p>
                  </div>

                  {focus.suggested_activities.length > 0 && (
                    <div>
                      <h3 className="text-xl font-serif text-primary-900 mb-3">Activities to try</h3>
                      <ul className="space-y-2">
                        {focus.suggested_activities.map((activity, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-medium mt-0.5">
                              {i + 1}
                            </span>
                            <span className="text-base text-stone-700 flex-1">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {focus.activity_ids && focus.activity_ids.length > 0 && (
                    <div>
                      <h3 className="text-xl font-serif text-primary-900 mb-3">
                        Selected Activities for Home
                        {child?.glp_stage && (
                          <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-normal">
                            Stage {child.glp_stage} adapted
                          </span>
                        )}
                      </h3>
                      <div className="space-y-4">
                        {allActivities
                          .filter(activity => focus.activity_ids.includes(activity.id))
                          .map((activity, index) => {
                            const displayActivity = getActivityForChild(activity);
                            return (
                              <div key={activity.id} className={`p-5 rounded-2xl border ${displayActivity.isCustomized ? 'bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200' : 'bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20'}`}>
                                <div className="flex items-start gap-3 mb-3">
                                  <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-white text-sm font-bold mt-1">
                                    {index + 1}
                                  </span>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-lg font-medium text-primary-900">{displayActivity.name}</h4>
                                      {displayActivity.isCustomized && (
                                        <span className="px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">
                                          Customized for {child?.name}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-stone-600">{activity.play_type}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`share-focus-activity-btn-${activity.id}`}
                                    onClick={() => handleShareActivity(activity)}
                                    disabled={sharingActivityId === activity.id}
                                    className="rounded-full h-9 px-3 text-primary hover:text-primary"
                                    title="Share with parent"
                                  >
                                    <Share2 className={`w-4 h-4 mr-1 ${sharingActivityId === activity.id ? 'animate-pulse' : ''}`} />
                                    <span className="text-xs font-medium">Share</span>
                                  </Button>
                                </div>
                              
                                {displayActivity.language_examples && displayActivity.language_examples.length > 0 && (
                                  <div className="mt-3 p-3 bg-white rounded-xl">
                                    <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-2">Language to model:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {(child?.glp_stage 
                                        ? adaptLanguageForStage(displayActivity.language_examples, child.glp_stage)
                                        : displayActivity.language_examples
                                      ).slice(0, 4).map((phrase, i) => (
                                        <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                                          &quot;{phrase}&quot;
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              
                                {displayActivity.follow_lead_guidance && (
                                  <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                                    <p className="text-xs font-bold text-blue-900 mb-1">Follow their lead:</p>
                                    <p className="text-sm text-blue-800">{displayActivity.follow_lead_guidance}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {focus.modeled_language.length > 0 && (
                    <div>
                      <h3 className="text-xl font-serif text-primary-900 mb-3">
                        Language to model
                        {child?.glp_stage && (
                          <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-normal">
                            Stage {child.glp_stage} adapted
                          </span>
                        )}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(child?.glp_stage 
                          ? adaptLanguageForStage(focus.modeled_language, child.glp_stage)
                          : focus.modeled_language
                        ).map((phrase, i) => (
                          <div key={i} className="p-4 bg-primary/5 rounded-2xl">
                            <p className="text-base text-primary-900 font-medium">"{phrase}"</p>
                          </div>
                        ))}
                      </div>
                      {child?.glp_stage && (
                        <div className="mt-4 p-4 bg-purple-50 rounded-2xl">
                          <p className="text-sm text-purple-900">
                            <strong>Stage {child.glp_stage} Guidance:</strong> {getStageTip(child.glp_stage)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl border border-primary/10">
                <p className="font-accent text-2xl text-center text-primary-800 leading-relaxed -rotate-1">
                  {focus.reassurance_message}
                </p>
              </Card>
            </div>
          )}
        </motion.div>
      </main>

      {/* AI Feedback Popup */}
      {showAIFeedback && (
        <div className="fixed bottom-20 right-4 z-50 w-72 shadow-lg">
          <AIFeedbackPopup type="activity_generation" context="Weekly Focus AI Generator" onDismiss={() => setShowAIFeedback(false)} />
        </div>
      )}

      {/* AI Activity Generator Dialog */}
      <Dialog open={showAIGeneratorDialog} onOpenChange={setShowAIGeneratorDialog}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-generator-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Generate Activity with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Create a personalized activity for {child?.name} based on their interests and goals. The activity will be added directly to your weekly focus.
            </p>
            
            {/* Child Interests */}
            <div className="space-y-2">
              <Label htmlFor="ai-interests">Child's Interests</Label>
              <Input
                id="ai-interests"
                value={aiFormData.child_interests}
                onChange={(e) => setAiFormData({ ...aiFormData, child_interests: e.target.value })}
                className="h-12 rounded-2xl"
                placeholder="dinosaurs, bubbles, cars, music..."
                data-testid="ai-interests-input"
              />
              <p className="text-xs text-stone-500">Separate multiple interests with commas</p>
            </div>
            
            {/* Communication Style */}
            <div className="space-y-2">
              <Label>Communication Style</Label>
              <Select 
                value={aiFormData.communication_style} 
                onValueChange={(value) => setAiFormData({ ...aiFormData, communication_style: value })}
              >
                <SelectTrigger className="h-12 rounded-2xl" data-testid="ai-comm-style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GLP">Gestalt Language Processor (GLP)</SelectItem>
                  <SelectItem value="ALP">Analytic Language Processor (ALP)</SelectItem>
                  <SelectItem value="mixed">Mixed Profile</SelectItem>
                </SelectContent>
              </Select>
              {child?.glp_stage && (
                <p className="text-xs text-purple-600">
                  GLP Stage {child.glp_stage} - Language models will match this stage
                </p>
              )}
            </div>
            
            {/* Age & Regulation Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Age (NT reference) */}
              <div className="space-y-2">
                <Label>Age (for neurotypical developmental reference)</Label>
                <Input
                  type="number"
                  min="1"
                  max="18"
                  placeholder="e.g., 4"
                  value={aiFormData.age || ''}
                  onChange={(e) => setAiFormData({ ...aiFormData, age: e.target.value })}
                  className="h-10 rounded-xl"
                  data-testid="ai-age-input"
                />
                <p className="text-xs text-stone-400">Only needed for neurotypical clients — neurodiverse children follow their own communication timeline</p>
              </div>
            
              {/* Regulation Preference */}
              <div className="space-y-2">
                <Label>Regulation preference</Label>
                <Select 
                  value={aiFormData.regulation_preference} 
                  onValueChange={(value) => setAiFormData({ ...aiFormData, regulation_preference: value })}
                >
                  <SelectTrigger className="h-10 rounded-xl" data-testid="ai-regulation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any</SelectItem>
                    <SelectItem value="calming">Calming / Low arousal</SelectItem>
                    <SelectItem value="alerting">Alerting / High energy</SelectItem>
                    <SelectItem value="organizing">Organizing / Structured</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Goals Selection */}
            {childGoals.length > 0 && (
              <div className="space-y-2">
                <Label>Target Goals (select one or more)</Label>
                <div className="max-h-36 overflow-y-auto border border-stone-200 rounded-2xl p-3 space-y-2">
                  {childGoals.map((goal, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Checkbox
                        id={`goal-${idx}`}
                        checked={aiFormData.selected_goals.includes(goal)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setAiFormData({ ...aiFormData, selected_goals: [...aiFormData.selected_goals, goal] });
                          } else {
                            setAiFormData({ ...aiFormData, selected_goals: aiFormData.selected_goals.filter(g => g !== goal) });
                          }
                        }}
                        data-testid={`ai-goal-checkbox-${idx}`}
                      />
                      <label htmlFor={`goal-${idx}`} className="text-sm text-stone-700 cursor-pointer leading-tight">
                        {goal.length > 80 ? `${goal.substring(0, 80)}...` : goal}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Custom Goal */}
            <div className="space-y-2">
              <Label htmlFor="ai-custom-goal">Custom Goal (optional)</Label>
              <Input
                id="ai-custom-goal"
                value={aiFormData.custom_goal}
                onChange={(e) => setAiFormData({ ...aiFormData, custom_goal: e.target.value })}
                className="h-12 rounded-2xl"
                placeholder="e.g., Increase 2-word combinations during play"
                data-testid="ai-custom-goal-input"
              />
            </div>
            
            {/* Materials */}
            <div className="space-y-2">
              <Label htmlFor="ai-materials">Preferred Materials (optional)</Label>
              <Input
                id="ai-materials"
                value={aiFormData.materials}
                onChange={(e) => setAiFormData({ ...aiFormData, materials: e.target.value })}
                className="h-12 rounded-2xl"
                placeholder="e.g., sensory bin, playdough, books..."
                data-testid="ai-materials-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowAIGeneratorDialog(false)}
                className="flex-1 rounded-full"
                disabled={aiGenerating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAIGenerate}
                className="flex-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                disabled={aiGenerating || (!aiFormData.child_interests && !aiFormData.custom_goal && aiFormData.selected_goals.length === 0)}
                data-testid="ai-generate-btn"
              >
                {aiGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Activity
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize Activity Dialog */}
      <Dialog open={showCustomizeDialog} onOpenChange={setShowCustomizeDialog}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="customize-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              Customize for {child?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Create a personalized version of this activity. The original will remain unchanged in the global library.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="custom-name">Activity Name</Label>
              <Input
                id="custom-name"
                value={customizeForm.name}
                onChange={(e) => setCustomizeForm({ ...customizeForm, name: e.target.value })}
                className="h-12 rounded-2xl"
                data-testid="customize-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-language">Language Examples (one per line)</Label>
              <Textarea
                id="custom-language"
                value={customizeForm.language_examples}
                onChange={(e) => setCustomizeForm({ ...customizeForm, language_examples: e.target.value })}
                className="rounded-2xl min-h-24"
                placeholder="More bubbles!&#10;Pop pop!&#10;Ready set go!"
                data-testid="customize-language-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-guidance">Follow Their Lead Guidance</Label>
              <Textarea
                id="custom-guidance"
                value={customizeForm.follow_lead_guidance}
                onChange={(e) => setCustomizeForm({ ...customizeForm, follow_lead_guidance: e.target.value })}
                className="rounded-2xl min-h-20"
                data-testid="customize-guidance-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-notes">Notes for Parent</Label>
              <Textarea
                id="custom-notes"
                value={customizeForm.notes}
                onChange={(e) => setCustomizeForm({ ...customizeForm, notes: e.target.value })}
                className="rounded-2xl min-h-16"
                placeholder="Optional notes specific to this child..."
                data-testid="customize-notes-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCustomizeDialog(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCustomization}
                className="flex-1 rounded-full"
                data-testid="save-customization-btn"
              >
                Save Customization
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
